import { Divider, Padding, Row, SizedBox } from "../../../common/components/CommonBaseComponents";
import { C2Text, C3Text } from "../../../common/components/text/CustomText";
import { TitleAndInput } from "../../../common/components/input/TitleAndInput";
import { useState } from "react";
import CustomModal from "../../../common/components/modal/CustomDetailModal";
import CustomPopup from "../../../common/components/popup/CustomPopup";
import { TitleAndRadio } from "../../../common/components/radio/TitleAndRadio";
import Translate from "../../../common/translate/Translate";
import PrimaryButton from "../../../common/components/button/PrimaryButton";
import SecondaryButton from "../../../common/components/button/SecondaryButton";
import { Theme } from "../../../common/Theme";
import UserService from "../../../service/UserService";

// value는 API role(snake_case) 그대로, label은 화면 표시 텍스트
// 조회(비편집) 시 라벨
const displayTypeOptions = [
    { value: "franchise_admin", label: "Franchise Admin" },
    { value: "call_center", label: "Call Center" },
    { value: "store_owner", label: "Shop Owner" },
];
// 편집 시 라벨 (Admin / Call Center / Shop Owner 고정)
const editTypeOptions = [
    { value: "franchise_admin", label: "Admin" },
    { value: "call_center", label: "Call Center" },
    { value: "store_owner", label: "Shop Owner" },
];

const statusOptions = [
    { value: "Enable", label: Translate("marketing.quota.enable") },
    { value: "Disable", label: Translate("marketing.quota.disable") },
];

export default function UserDetailModal({ isOpen, onClose, dto, onSave }) {
    const service = new UserService();

    const [type, setType] = useState(dto?.role || "");
    const [email, setEmail] = useState(dto?.email || "");
    const [status, setStatus] = useState(dto?.status || "Enable");
    const [name, setName] = useState(dto?.name || "-");
    const [contact, setContact] = useState(dto?.contact || "-");

    const [isEditMode, setIsEditMode] = useState(false);

    // 목록 응답 row에 key가 없던 필드는 상세에서도 숨김 (기본값 true: 다른 호출부 영향 없음)
    const hasStatus = dto?.hasStatus ?? true;
    const hasName = dto?.hasName ?? true;
    const hasContact = dto?.hasContact ?? true;

    // 상세 필드는 목록 응답(dto)에서 채운다. (모달은 매 오픈 시 remount되어 useState 초기값으로 반영됨)

    // Confirm Modal
    const [isConfirmPopupOpen, setIsConfirmPopupOpen] = useState(false);

    const handleConfirmClose = () => {
        setIsConfirmPopupOpen(false);
    };

    const handleConfirmOk = () => {
        const request = { userNo: dto?.id };
        if (name && name !== "-") request.name = name;
        if (contact && contact !== "-") request.phone = contact;
        if (hasStatus) request.enabled = status === "Enable";

        service
            .updateTargetUserInfo({ request })
            .then(() => {
                setIsEditMode(false);
                setIsConfirmPopupOpen(false);
                if (onSave) onSave();
            })
            .catch((error) => {
                console.error("Error updating user info:", error);
                setIsConfirmPopupOpen(false);
            });
    };

    // Cancel Modal
    const [isCancelPopupOpen, setIsCancelPopupOpen] = useState(false);
    const [cancelActionType, setCancelActionType] = useState("button");

    const handleCancelClose = () => {
        setIsCancelPopupOpen(false);
    };

    const handleCancelOk = () => {
        setIsCancelPopupOpen(false);

        if (cancelActionType === "modalClose") {
            onClose();
        } else {
            if (isEditMode) {
                onClose();
            } else {
                setIsEditMode(false);
            }
        }
        setCancelActionType("button");
    };

    return (
        <CustomModal
            title={"User Detail"}
            isOpen={isOpen}
            onClose={
                isEditMode
                    ? () => {
                        setCancelActionType("modalClose");
                        setIsCancelPopupOpen(true);
                    }
                    : onClose
            }
            width={"552px"}
            side={"right"}
            height={"90vh"}
            bottomButtons={
                <Row justifyContent={"flex-end"} width={"100%"} padding={"22px 20px"}>
                    <SecondaryButton
                        text={Translate("common.cancel")}
                        width={"70px"}
                        padding={"7.75px 14.5px"}
                        onClick={() => {
                            if (!isEditMode) {
                                onClose();
                            } else {
                                setIsCancelPopupOpen(true);
                            }
                        }}
                    />
                    <SizedBox width={"10px"} />
                    <PrimaryButton
                        text={isEditMode ? Translate("common.save") : Translate("common.edit")}
                        width={"70px"}
                        padding={"7.75px 14.5px"}
                        onClick={() => {
                            if (!isEditMode) {
                                setIsEditMode(true);
                            } else {
                                setIsConfirmPopupOpen(true);
                            }
                        }}
                    />
                </Row>
            }
        >
            <SizedBox
                width={"100%"}
                height={"auto"}
                border={"solid 1px #E6E6E9"}
                borderRadius={"16px"}
                alignItem={"flex-start"}
                justifyContent={"flex-start"}
                padding={"22px 0 0"}
            >
                <Row alignItem={"flex-start"} justifyContent={"space-between"} width={"100%"} padding={"0 20px"}>
                    <C2Text fontWeight={"600"}>User Info</C2Text>
                    <C3Text fontColor={Theme.color.gray500}>{dto?.registeredDate}</C3Text>
                </Row>
                <SizedBox height={"20px"} />
                <Divider />
                <Padding padding={isEditMode ? "23px 20px" : "11px 20px 11px"} style={{ width: "100%" }}>
                    <TitleAndRadio
                        title={"Type"}
                        options={isEditMode ? editTypeOptions : displayTypeOptions}
                        selectedOption={type}
                        setSelectedOption={setType}
                        isEditMode={isEditMode}
                        readOnly // role 변경 불가: 기존 role 체크 상태로 고정
                    />
                    <Divider
                        marginTop={isEditMode ? "23px" : "11px"}
                        marginBottom={"11px"}
                        lineStyle={"custom-dotted"}
                    />
                    <TitleAndInput
                        title={"ID"}
                        type={"normal"}
                        placeholder={""}
                        inputValue={email}
                        setInputValue={setEmail}
                        disabled={true}
                    />
                    {hasStatus && (
                        <>
                            <Divider
                                marginTop={"11px"}
                                marginBottom={isEditMode ? "23px" : "11px"}
                                lineStyle={"custom-dotted"}
                            />
                            <TitleAndRadio
                                title={"Status"}
                                options={statusOptions}
                                selectedOption={status}
                                setSelectedOption={setStatus}
                                isEditMode={isEditMode}
                            />
                        </>
                    )}
                    {hasName && (
                        <>
                            <Divider
                                marginTop={isEditMode ? "23px" : "11px"}
                                marginBottom={"11px"}
                                lineStyle={"custom-dotted"}
                            />
                            <TitleAndInput
                                title={"name"}
                                type={"normal"}
                                placeholder={""}
                                inputValue={name}
                                setInputValue={setName}
                                disabled={!isEditMode}
                            />
                        </>
                    )}
                    {hasContact && (
                        <>
                            <Divider marginTop={"11px"} marginBottom={"11px"} lineStyle={"custom-dotted"} />
                            <TitleAndInput
                                title={"Contact"}
                                type={"normal"}
                                placeholder={""}
                                inputValue={contact}
                                setInputValue={setContact}
                                disabled={!isEditMode}
                            />
                        </>
                    )}
                </Padding>
            </SizedBox>
            <CustomPopup
                isOpen={isConfirmPopupOpen}
                onClose={handleConfirmClose}
                onConfirm={handleConfirmOk}
                message={"Are you sures you want to save?"}
            />
            <CustomPopup
                isOpen={isCancelPopupOpen}
                onClose={handleCancelClose}
                onConfirm={handleCancelOk}
                message={Translate("popup.cancel")}
            />
        </CustomModal>
    );
}
